import * as Sentry from '@sentry/angular';
import { enableProdMode, NgModuleRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { PORTAL_RELEASE_VERSION } from '@app-core/constants/constants';
import { applyClientColorsFonts, applyManifest, extractConfig, setClientConfig, setClientIndex } from '@config/client-config';
import { initAssetResolver } from '@app-shared/services/asset-resolver/asset-resolver.service';
import { performInitialNavigationForGeotab, subscribeNavigateToListnerForGeotab } from './app/geotab/geotab-utils';

function deepFreeze<T>(obj: T): T {
  Object.freeze(obj);

  Object.getOwnPropertyNames(obj).forEach((prop) => {
    const value = (obj as any)[prop];
    if (value !== null && (typeof value === 'object' || typeof value === 'function') && !Object.isFrozen(value)) {
      deepFreeze(value);
    }
  });

  return obj;
}

// ── Local dev fallback (lmpresales config) ──
// Used when /client-ui-config is unavailable locally (backend not running).
// This lets developers boot the app without the runtime config API.
const LOCAL_DEV_FALLBACK_CONFIG = {
  title: 'RideView Fleet Portal',
  themeColor: '#2898a2',
  fontFamily: 'Inter',
  cognito: {
    cognitoConfigurations: {
      clientName: 'lmpresales',
      resellerId: 'lightmetrics',
      showLogo: true,
      showSurveyButton: true,
      showUserProfile: true,
      showManageDriversTab: true,
      showChallengesTab: true,
      showFeatureAnnouncement: true,
      showLogoutButton: true,
      showLandingPage: true,
      showPartnerLogo: false,
      showSecurityButton: true,
      showHomePageAnnouncement: false,
      showFeedbackButton: false,
      showUserTypeEmail: true,
      showTheme: true,
      showHeader: true,
      showSideNav: true,
      showHelpLink: false,
      showCustomLoginPage: true,
      showCopyrightText: true,
      showPrivacyPolicyLink: true,
      isAssetCentric: false,
      disableSuprsendNotification: false,
      disableSentry: true,
      isGeotabEnv: false,
      useScripts: false,
      useCustomCss: false,
      'custom-styles': 'assets/local/custom-styles.css',
      lightPrimary: '#2898a2',
      lightPrimaryContrast: '#ffffff',
      darkPrimary: '#2898a2',
      darkPrimaryContrast: '#ffffff',
      lightAccent: '#8b2682',
      lightAccentContrast: '#ffffff',
      darkAccent: '#e192d6',
      darkAccentContrast: '#212121',
      pageBackground: '#2898a22f',
      largeFontSize: '20px',
      headingFontSize: '16px',
      bodyFontSize: '14px',
      captionFontSize: '12px',
      lightFontWeight: '300',
      normalFontWeight: '400',
      mediumFontWeight: '500',
      boldFontWeight: '600',
      logo: {
        alt: 'LightMetrics',
        darkLogo: 'assets/local/dark-logo.svg',
        height: '36px',
        lightLogo: 'assets/local/light-logo.svg',
        loginPageHeight: '36px',
        loginPageLogo: 'assets/local/login-page-logo.jpg',
        loginPageWidth: '113px',
        mobileModeDarkLogo: 'assets/local/mobile-mode-dark-logo.svg',
        mobileModeHeight: '36px',
        mobileModeLightLogo: 'assets/local/mobile-mode-light-logo.svg',
        mobileModeWidth: '40px',
        width: '148px',
      },
      partnerLogo: {
        alt: 'LightMetrics',
        height: '35px',
        mobileModeHeight: '35px',
        mobileModeWidth: '55px',
        partnerDarkLogo: 'assets/local/partner-dark-logo.png',
        partnerLightLogo: 'assets/local/partner-light-logo.png',
        partnerMobileModeDarkLogo: 'assets/local/partner-mobile-mode-dark-logo.png',
        partnerMobileModeLightLogo: 'assets/local/partner-mobile-mode-light-logo.png',
        width: '55px',
      },
      favicon: 'assets/local/favicon.ico',
      allowedRoutes: [
        'home',
        'safety-events',
        'trips',
        'live-view',
        'coaching',
        'video-requests',
        'drivers',
        'challenges',
        'reports',
        'assets',
        'user-management',
        'configurations',
      ],
      authRoutes: ['callback', 'tsplogin', 'admin-login', 'login', 'unauthorized-error', 'custom-login'],
      wildcardRoute: 'callback',
      allowedMobileRoutes: ['safety-events', 'live-view', 'video-requests', 'notifications-page'],
      defaultMapCoordinates: { lat: 40.25, lng: -74.5 },
      externalEventsLabel: 'Panic Button',
      defaultStorage: 'LocalStorage',
      devicePrimaryKey: 'deviceId',
      assetConfig: {
        mode: 'RELATIVE' as const,
      },
      allowedParentOrigins: [],
      landingPageDetails: {
        backgroundImage: 'assets/local/login-page-bg.jpg',
        brandImage: 'assets/local/light-logo.svg',
        headerImage: 'assets/local/login-page-header.svg',
        tagline: 'lmpresalesTagline',
        disableRegionSelection: false,
        defaultUserCountry: 'US',
        footer: {
          showFooterPrivacyText: true,
          showPrivacyPolicy: true,
          phone: '1 888 441 6699',
          companyName: 'LightMetrics, Inc.',
          footerType: 'default',
          privacyPolicyLink: 'https://www.lightmetrics.co/privacy-policy/',
          email: 'support@lightmetrics.co',
        },
      },
    },
    cognitoEnvironment: {
      production: false,
      legacy_api_server_url: 'http://localhost:2999',
      intermediate_server_url: 'http://localhost:6001',
      redirect_url: '',
      callbackURL: 'http://localhost:4200/callback',
      scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
      env: 'qa',
      translation_version: 'latest',
      translation_env: 'dev',
      resellerId: 'lightmetrics',
      isGeotabEnv: false,
      resellerConfig: {
        'ap-south-1': {
          userPoolId: 'ap-south-1_QsbkMvioC',
          userPoolClientId: '5v3b3edqdkcl3l782jn0lfib6c',
        },
        'eu-west-2': {
          userPoolId: 'eu-west-2_T1kAB9Q4N',
          userPoolClientId: '7vbqdeqgd2tlo8sc7k6hiulehv',
        },
        'ap-southeast-2': {
          userPoolId: 'ap-southeast-2_CuMKMK9Q6',
          userPoolClientId: '1lckkrqc1adluifr90on1m06p4',
        },
      },
      cognitoConfigs: {
        'ap-south-1': {
          clientID: '6rue7n3aov84kidhprool996c2',
          domain: 'https://fleet-managers.auth.ap-south-1.amazoncognito.com',
        },
        'eu-west-2': {
          clientID: '7vbqdeqgd2tlo8sc7k6hiulehv',
          domain: 'https://fleet-portal.auth.eu-west-2.amazoncognito.com',
        },
      },
    },
  },
  sso: {
    ssoConfigurations: {
      clientName: 'lmpresales',
      resellerId: 'lightmetrics',
      showLogo: true,
      showSurveyButton: true,
      showUserProfile: true,
      showManageDriversTab: true,
      showChallengesTab: true,
      showFeatureAnnouncement: true,
      showLogoutButton: true,
      showLandingPage: true,
      showPartnerLogo: false,
      showSecurityButton: true,
      showHomePageAnnouncement: false,
      showFeedbackButton: false,
      showUserTypeEmail: true,
      showTheme: true,
      showHeader: true,
      showSideNav: true,
      showHelpLink: false,
      showCustomLoginPage: true,
      showCopyrightText: true,
      showPrivacyPolicyLink: true,
      isAssetCentric: false,
      disableSuprsendNotification: false,
      disableSentry: true,
      isGeotabEnv: false,
      useScripts: false,
      lightPrimary: '#2898a2',
      lightPrimaryContrast: '#ffffff',
      darkPrimary: '#2898a2',
      darkPrimaryContrast: '#ffffff',
      lightAccent: '#8b2682',
      lightAccentContrast: '#ffffff',
      darkAccent: '#e192d6',
      darkAccentContrast: '#212121',
      pageBackground: '#2898a22f',
      largeFontSize: '20px',
      headingFontSize: '16px',
      bodyFontSize: '14px',
      captionFontSize: '12px',
      lightFontWeight: '300',
      normalFontWeight: '400',
      mediumFontWeight: '500',
      boldFontWeight: '600',
      logo: {
        loginPageLogo: 'assets/local/login-page-logo.jpg',
        lightLogo: 'assets/local/light-logo.svg',
        darkLogo: 'assets/local/dark-logo.svg',
        alt: 'LightMetrics',
        width: '148px',
        height: '36px',
        loginPageWidth: '113px',
        loginPageHeight: '36px',
      },
      favicon: 'assets/local/favicon.ico',
      allowedRoutes: [
        'home',
        'safety-events',
        'trips',
        'live-view',
        'coaching',
        'video-requests',
        'drivers',
        'challenges',
        'reports',
        'assets',
        'configurations',
      ],
      authRoutes: ['callback', 'tsplogin', 'admin-login', 'login', 'unauthorized-error', 'custom-login'],
      wildcardRoute: 'callback',
      allowedMobileRoutes: ['safety-events', 'live-view', 'video-requests', 'notifications-page'],
      defaultMapCoordinates: { lat: 40.25, lng: -74.5 },
      externalEventsLabel: 'Panic Button',
      defaultStorage: 'LocalStorage',
      devicePrimaryKey: 'deviceId',
      assetConfig: {
        mode: 'RELATIVE' as const,
      },
      allowedParentOrigins: [],
      landingPageDetails: {
        backgroundImage: {
          src: 'assets/local/login-page-bg.jpg',
        },
        brandImage: {
          src: 'assets/local/dark-logo.svg',
        },
        headerImage: {
          src: 'assets/local/login-page-header.svg',
        },
        tagline: 'lmpresalesTagline',
        disableRegionSelection: false,
        defaultUserCountry: 'US',
        allowedRegions: ['ap-south-1', 'eu-west-2', 'ap-southeast-2'],
      },
    },
    ssoEnvironment: {
      production: false,
      legacy_api_server_url: 'http://localhost:2999',
      intermediate_server_url: 'https://white-dt1-local.lightmetrics.co:10455',
      redirect_url: '',
      callbackURL: 'http://localhost:4200/callback',
      scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
      env: 'local',
      translation_version: 'latest',
      translation_env: 'dev',
      resellerId: 'lightmetrics',
      isGeotabEnv: false,
      resellerConfig: {
        'ap-south-1': {
          userPoolId: 'ap-south-1_QsbkMvioC',
          userPoolClientId: '5v3b3edqdkcl3l782jn0lfib6c',
        },
        'eu-west-2': {
          userPoolId: 'eu-west-2_T1kAB9Q4N',
          userPoolClientId: '7vbqdeqgd2tlo8sc7k6hiulehv',
        },
        'ap-southeast-2': {
          userPoolId: 'ap-southeast-2_CuMKMK9Q6',
          userPoolClientId: '1lckkrqc1adluifr90on1m06p4',
        },
      },
      cognitoConfigs: {
        'ap-south-1': {
          clientID: '6rue7n3aov84kidhprool996c2',
          domain: 'https://fleet-managers.auth.ap-south-1.amazoncognito.com',
        },
        'eu-west-2': {
          clientID: '7vbqdeqgd2tlo8sc7k6hiulehv',
          domain: 'https://fleet-portal.auth.eu-west-2.amazoncognito.com',
        },
      },
    },
  },
};

// ── Retry-aware config fetcher ──
async function fetchConfigWithRetry(url: string, retries: number = 1): Promise<any> {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          // Send the iframe's full URL so the server can identify the client
          // even when host/origin headers don't match a DynamoDB entry
          // (e.g., Geotab deploys on their own domain, parent embeds app in iframe)
          'x-client-url': window.location.href,
          'x-client-referrer': document.referrer || '',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (err) {
      if (attempt === retries) throw err;
      // Exponential backoff: 2s, 4s, 8s
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
}

// ── Splash screen helpers ──
function hideSplash(): void {
  const splash = document.getElementById('app-splash');
  if (splash) splash.style.display = 'none';
  const appRoot = document.querySelector('app-root') as HTMLElement;
  if (appRoot) appRoot.style.display = 'block';
}

function showSplashError(message?: string): void {
  const spinner = document.querySelector('.splash-spinner') as HTMLElement;
  const text = document.querySelector('.splash-text') as HTMLElement;
  const errorBlock = document.getElementById('splash-error');
  const errorMessage = document.getElementById('splash-error-message');

  if (spinner) spinner.style.display = 'none';
  if (text) text.style.display = 'none';
  if (errorBlock) errorBlock.style.display = 'block';
  if (errorMessage && message) errorMessage.textContent = message;
}

// ── Geotab bootstrap (handles race condition) ──
function bootstrapGeotab(): void {
  let ngModuleRef: NgModuleRef<AppModule> | null = null;

  function doBootstrap(): void {
    if (ngModuleRef) return;
    const main = document.querySelector('main');
    const appRoot = main?.querySelector('app-geotab-root');
    if (!appRoot) main?.appendChild(document.createElement('app-geotab-root'));

    hideSplash();

    platformBrowserDynamic()
      .bootstrapModule(AppModule)
      .then((_) => {
        ngModuleRef = _;
        subscribeNavigateToListnerForGeotab(ngModuleRef);
        performInitialNavigationForGeotab(ngModuleRef);
      })
      .catch((err) => console.error('Geotab bootstrap failed:', err));
  }

  // Listen for future focus events
  window.addEventListener('rideview:bootstrap-app-module', doBootstrap);

  window.addEventListener('rideview:destroy-app-module', () => {
    ngModuleRef?.destroy();
    ngModuleRef = null;
  });

  // Handle race condition: focus() may have fired BEFORE main.ts registered the listener.
  // The index.html script sets window.__GEOTAB_FOCUS_FIRED__ = true when focus() fires.
  // If credentials are already in sessionStorage, bootstrap immediately.
  if ((window as any).__GEOTAB_FOCUS_FIRED__ && sessionStorage.getItem('rideview:credentials')) {
    doBootstrap();
  }
}

// ── Main bootstrap ──
(async () => {
  try {
    // 1. Fetch runtime config from API (with retry + local dev fallback)
    let config;
    try {
      const response = await fetchConfigWithRetry(`${environment.intermediate_server_url}/client-ui-config`);
      if (!response?.config) {
        throw new Error('Invalid response: config missing');
      }
      config = response.config;
    } catch (fetchErr) {
      if (environment.env === 'local') {
        console.warn('[dev] /client-ui-config unavailable — using lmpresales fallback config');
        config = LOCAL_DEV_FALLBACK_CONFIG;
      } else {
        throw fetchErr;
      }
    }

    const SSO_PATHS = ['/sso', '/iframe-login', '/iframe-implicit-login', '/rsa-sso-login', '/auth0-sso-login', '/geotab-login'];
    const isSSO = SSO_PATHS.some((p) => window.location.pathname.startsWith(p));
    const finalConfig = extractConfig(config, isSSO);

    initAssetResolver(finalConfig.configuration);

    if (finalConfig.configuration.useScripts) {
      (window as any).__CLARITY_ID__ = deepFreeze(finalConfig.configuration.clarityId);
    }

    // 2. Apply branding early (before Angular loads)
    setClientIndex(finalConfig);
    applyClientColorsFonts(finalConfig);
    applyManifest(finalConfig);

    // 3. Save globally for runtime access
    setClientConfig(finalConfig);

    if (environment.production) {
      enableProdMode();

      if (!Boolean(finalConfig.configuration.disableSentry) && environment?.env === 'prod') {
        Sentry.init({
          release: PORTAL_RELEASE_VERSION,
          sampleRate: 0.5,
          dsn: 'https://ce6035b77b99036d6c97c2898d1d0bcd@o4507763074138112.ingest.us.sentry.io/4507763076038656',
          integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
          tracesSampleRate: 0.1,
          tracePropagationTargets: [/^https:\/\/*.lightmetrics\.co\//],
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 0.1,
        });
      }
    }

    // 4. Bootstrap Angular
    if (finalConfig?.environments?.isGeotabEnv) {
      bootstrapGeotab();
    } else {
      await platformBrowserDynamic()
        .bootstrapModule(AppModule)
        .then(() => {
          hideSplash();
          if ('serviceWorker' in navigator && environment.production) {
            navigator.serviceWorker.register('/ngsw-worker.js');
          }
        })
        .catch((err) => {
          console.error('Angular bootstrap failed:', err);
          showSplashError('The application failed to start. Please refresh the page.');
        });
    }
  } catch (err) {
    console.error('Failed to load application configuration:', err);
    showSplashError('Unable to load the application configuration. Please check your connection and try again.');
  }
})();
